import React from "react";
import Modal from "@material-ui/core/Modal";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import styled from "styled-components";
import FlagIcon from "@material-ui/icons/Flag";
import TablePaginationActions from "@material-ui/core/TablePagination/TablePaginationActions";
import { IconButton, makeStyles, TablePagination } from "@material-ui/core";
import { Delete, Edit } from "@material-ui/icons";
import { Link } from "react-router-dom";
import {
  useTable,
  useRowSelect,
} from "react-table";
import { CustomCheckbox, modal } from "../../../util";
import StatusIndicator from "./StatusIndicator";
import apiHelper from "../../../util/ApiHelper";
import { useLoading } from "../../../util/LoadingProvider";
import "./index.scss";
import EmailForm from "./EmailForm";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  iconBtn: {
    color: "#CCCCCC",
    margin: 5,
  },
  status: {
    textTransform: "lowercase",
    "&:first-letter": {
      textTransform: "uppercase",
    },
  },
  modal: {
    background: "#ffffff",
    width: "874px",
    top: `50%`,
    left: `50%`,
    transform: `translate(-50%, -50%)`,
    position: "absolute",
    padding: theme.spacing(3),
    margin: theme.spacing(3),
  },
}));

const StatusFilter = styled.ul`
  list-style: none;
  display: inline-flex;
  padding: 0;
  li {
    button {
      border: 0px;
      background: transparent;
      color: #306f9d;
      font-size: 13px;
      line-height: 15px;
      padding: 0 10px;
      cursor: pointer;
      &.active {
        font-weight: bold;
        color: #333333;
      }
    }
    &:not(:last-of-type) {
      button {
        border-right: 1px solid black;
      }
    }
    &:first-of-type {
      button {
        padding-left: 0;
      }
    }
  }
`;

const Dot = styled.div`
  display: block;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  margin: auto;
`;

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <CustomCheckbox type="checkbox" ref={resolvedRef} {...rest} />;
  }
);

function getKeyword(keyword) {
  return keyword ? `&keyword=${keyword}` : "";
}

function CallForEntryListingPage(props) {
  const { match, disabled, location } = props;
  const classes = useStyles();
  const [type, setType] = React.useState(null);
  const [keyword, setKeyword] = React.useState("");
  const [queryString, setQueryString] = React.useState("");
  const [data, setData] = React.useState([]);
  const { setLoading } = useLoading();

  // for sending email
  const [open, setOpen] = React.useState(false);
  const [entryId, setEntryId] = React.useState("");

  // for pagination
  const [page, setPage] = React.useState(
    (location.state && location.state.page) || 0
  );
  const [rowsPerPage, setRowsPerPage] = React.useState(
    (location.state && location.state.rowsPerPage) || 10
  );
  const [totalElements, setTotalElements] = React.useState(0);

  const CustomLink = React.useMemo(
    () =>
      React.forwardRef((linkProps, ref) => <Link ref={ref} {...linkProps} />),
    []
  );

  const filterHandler = (targetType) => () => {
    setType(targetType);
  };

  const triggerSearch = React.useCallback(() => {
    setQueryString(keyword);
  }, [keyword]);

  const deleteEntry = async (id) => {
    const decision = await modal.fire({
      title: "Delete Entry Application",
      text: "Are you sure to delete this entry application?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        const result = await apiHelper.hkiff.deleteEntryApplication(id);
        if (result.data.code === 0) {
          await getCallForEntries();
          toast.dark("Entry application deleted.");
        }
        console.log(result);
      } catch (err) {
        console.log(err);
      }
    }
  };

  const getCallForEntries = React.useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.getEntriesApplications(
        null,
        page,
        (page + 1) * rowsPerPage,
        rowsPerPage,
        queryString,
        type
      );
      setData(result.data.content.filter((x) => x));
      setTotalElements(result.data.totalElements);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, queryString, setLoading, type]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Ref.#",
        accessor: "referenceNum",
        className: "col_referenceNum",
      },
      {
        Header: "Original Title",
        accessor: "originalTitle",
        className: "col_originalTitle",
        Cell: ({ row, column, ...rest }) => {
          return (
            <Link
              to={{
                pathname: `${match.url}/${row.original.id}`,
                state: { page: page, rowsPerPage: rowsPerPage },
              }}
              className={column.className}
            >
              {row.original.originalTitle}
            </Link>
          );
        },
      },
      {
        Header: "Title",
        accessor: "englishTitle",
        className: "col_englishTitle",
        Cell: ({ row, column, ...rest }) => {
          return (
            <Link
              to={{
                pathname: `${match.url}/${row.original.id}`,
                state: { page: page, rowsPerPage: rowsPerPage },
              }}
              className={column.className}
            >
              {row.original.englishTitle}
            </Link>
          );
        },
      },
      {
        Header: "Director",
        accessor: "director",
      },
      {
        Header: "Cat.",
        accessor: "categoryDtl.category.titleEn",
        Cell: ({ row }) => {
          const regex = /.+(?=\([\w\s\d\+\-]+?\))/g; // Short Film Competition(below 20 mins)
          const found = row.original.categoryDtl.category.titleEn.match(regex); // ["Short Film Competition"]
          const shortCode = found[0] // "Short Film Competition"
            .split(" ") // ["Short","Film", "Competition"]
            .map((o) => o[0]) // ["S","F","C"]
            .join("") // SFc
            .toUpperCase(); // SFC
          return shortCode;
        },
      },
      {
        Header: "Screener Status",
        accessor: "submission.screenerVerified",
        className: "col_screenerStatus",
        Cell: ({ row }) => {
          if (row.original.submission?.screenerVerified === 0) {
            return <Dot color="#EA3323" />;
          }
          if (row.original.submission?.screenerVerified === 1) {
            return <Dot color="#4FAEEB" />;
          }
          return "";
        },
        disableGlobalFilter: true,
      },
      {
        Header: "Payment Method",
        accessor: "paymentRecord.0.paymentMethod",
        Cell: ({ row }) => {
          if (row.original?.paymentRecord && row.original?.paymentRecord[0]?.paymentMethod) {
            switch (row.original.paymentRecord[0].paymentMethod) {
              case "transfer":
                return "TT";
              case "money":
                return "bank draft";
              case "online":
                return "cityline";
              case "waived":
                return "waived";
              default:
                return null;
            }
          } else {
            return null;
          }
        },
      },
      {
        Header: "Payment Status",
        accessor: "submission.paymentVerified",
        className: "col_paymentStatus",
        Cell: ({ row }) => {
          if (row.original.submission?.paymentVerified === 0) {
            return <Dot color="#EA3323" />;
          }
          if (row.original.submission?.paymentVerified === 1) {
            return <Dot color="#4FAEEB" />;
          }
          return "";
        },
        disableGlobalFilter: true,
      },
      {
        Header: "Submission Status",
        accessor: "submission.submissionStatus",
        className: "col_subMissionStatus",
        Cell: ({ row }) => {
          if (row.original.submission?.submissionStatus === 0) {
            return <Dot color="#EA3323" />;
          }
          if (row.original.submission?.submissionStatus === 1) {
            return <Dot color="#F6C343" />;
          }
          if (row.original.submission?.submissionStatus === 2) {
            return <Dot color="#4FAEEB" />;
          }
          return "";
        },
        disableGlobalFilter: true,
      },
      {
        Header: "Send Email",
        accessor: "canSendEmail",
        className: "col_canSendEmail",
        Cell: ({ row }) => {
          if (row.original.canSendEmail) {
            return (
              <Button
                variant="outlined"
                color="primary"
                margin="dense"
                style={{ lineHeight: "1em" }}
                disabled={disabled}
                onClick={() => {
                  setOpen(true);
                  setEntryId(row.original.id);
                }}
              >
                Send
              </Button>
            );
          } else {
            return "TBC";
          }
        },
        disableGlobalFilter: true,
      },
      {
        Header: "Flag",
        accessor: "submission.flag",
        Cell: ({ row }) => {
          if (
            row.original.submission?.flag &&
            row.original.submission?.flag !== ""
          ) {
            return <FlagIcon style={{ color: row.original.submission.flag }} />;
          } else {
            return null;
          }
        },
        disableGlobalFilter: true,
      },
      {
        Header: "Create Time",
        accessor: "createTime",
        className: "col_createTime",
      },
    ],
    [page, rowsPerPage]
  );

  const { getTableProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
      },
      useRowSelect,
      (hooks) => {
        hooks.visibleColumns.push((columns) => [
          // Let's make a column for selection
          {
            id: "selection",
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div>
                <IndeterminateCheckbox
                  {...{ ...getToggleAllRowsSelectedProps(), disabled }}
                />
              </div>
            ),
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            Cell: ({ row }) => (
              <div>
                <IndeterminateCheckbox
                  {...{ ...row.getToggleRowSelectedProps(), disabled }}
                />
              </div>
            ),
          },
          ...columns,
          {
            id: "action",
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox
            Header: () => <div>Action</div>,
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            Cell: ({ row }) => (
              <div style={{ display: "inline-flex" }}>
                <IconButton
                  className={classes.iconBtn}
                  size="small"
                  component={CustomLink}
                  to={{
                    pathname: `${match.url}/${row.original.id}`,
                    state: { page: page, rowsPerPage: rowsPerPage },
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
                {disabled || (
                  <IconButton
                    className={classes.iconBtn}
                    size="small"
                    onClick={() => {
                      deleteEntry(row.original.id);
                    }}
                  >
                    <Delete fontSize="inherit" />
                  </IconButton>
                )}
              </div>
            ),
          },
        ]);
      }
    );

  React.useEffect(() => {
    getCallForEntries();
  }, [getCallForEntries]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Entry Management > Call for Entry
      </Typography>
      <StatusFilter>
        <li>
          <button
            className={type === null ? "active" : ""}
            type="button"
            onClick={filterHandler(null)}
          >
            <Typography variant="p">All</Typography>
          </button>
        </li>
        <li>
          <button
            className={type === "0" ? "active" : ""}
            type="button"
            onClick={filterHandler("0")}
          >
            <Typography variant="p">Submitted</Typography>
          </button>
        </li>
        <li>
          <button
            className={type === "1" ? "active" : ""}
            type="button"
            onClick={filterHandler("1")}
          >
            <Typography variant="p">Incomplete</Typography>
          </button>
        </li>
        <li>
          <button
            className={type === "2" ? "active" : ""}
            type="button"
            onClick={filterHandler("2")}
          >
            <Typography variant="p">In Consideration</Typography>
          </button>
        </li>
      </StatusFilter>
      <Grid container xs={12}>
        <StatusIndicator
          title="Screener status & Payment status"
          indicators={[
            { color: "#EA3323", label: "Pending" },
            { color: "#4FAEEB", label: "Confirmed" },
          ]}
        />
        <StatusIndicator
          title="Submission status"
          indicators={[
            { color: "#EA3323", label: "Submitted" },
            { color: "#F6C343", label: "Incomplete" },
            { color: "#4FAEEB", label: "In Consideration" },
          ]}
        />
      </Grid>

      {/* Start of Search Bar */}
      <Grid container justify="flex-end">
        <Grid container item xs={12} md={8}>
          <Box
            width="100%"
            component="span"
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Box display="flex">
              <Button
                variant="contained"
                color="primary"
                type="button"
                component={"a"}
                download
                href={`${
                  process.env.REACT_APP_API_HOSTNAME ||
                  "https://hkiff-api-dev.cloud.udev.hk"
                }/hkiff/admin/entries/applications/excelExport${getKeyword(
                  queryString
                )}`}
              >
                Export Entry Application
              </Button>
            </Box>
            <Box display="flex">
              <TextField
                id="outlined-error"
                label="Search"
                variant="outlined"
                margin="dense"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </Box>
            <Box display="flex">
              <Button
                variant="outlined"
                color="primary"
                type="button"
                margin="dense"
                onClick={triggerSearch}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Grid>

        {/* Start of Table */}
        <Grid container xs={12} style={{ marginTop: "4px" }}>
          <Table aria-label="simple table" component="ul" {...getTableProps()}>
            <TableHead component="div">
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <TableCell
                      {...column.getHeaderProps()}
                      className={`${column.className ?? ""}`}
                      style={{
                        paddingTop: 6,
                        paddingBottom: 6,
                        lineHeight: "1.1em",
                        height: "48px",
                      }}
                    >
                      {column.render("Header")}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                prepareRow(row);
                return (
                  <TableRow {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                      return (
                        <TableCell
                          {...cell.getCellProps()}
                          className={`${cell.column.className ?? ""}`}
                          style={{
                            paddingTop: 6,
                            paddingBottom: 6,
                            lineHeight: "1.1em",
                            height: "48px",
                          }}
                        >
                          {cell.render("Cell")}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Grid>
        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 10, 25, { label: "All", value: 9999999 }]}
          colSpan={7}
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{
            inputProps: { "aria-label": "rows per page" },
            native: true,
            style: {
              background: "white",
              border: "1px solid #CCCCCC",
              borderRadius: "4px",
            },
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          labelRowsPerPage="Show rows"
        />
      </Grid>
      {/* Send Email Modal Starts */}
      {open && (
        <Modal open={open} aria-labelledby="Send Email">
          <Box className={classes.modal}>
            <Grid container>
              <Typography
                variant="span"
                gutterBottom
                style={{ fontWeight: "bold" }}
              >
                Send Email
              </Typography>
            </Grid>
            <EmailForm
              mode="create"
              entryId={entryId}
              setOpen={setOpen}
              disabled={disabled}
            />
          </Box>
        </Modal>
      )}
      {/* Send Email Modal Ends */}
    </Box>
  );
}

export default CallForEntryListingPage;
